import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function WorkCalculator() {
  const [force, setForce] = useState("");
  const [distance, setDistance] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateWork = () => {
    const f = parseFloat(force);
    const d = parseFloat(distance);

    if (isNaN(f) || isNaN(d)) {
      toast.error("Please enter valid values");
      return;
    }

    const work = f * d;
    setResult(work);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setForce("");
    setDistance("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Work Calculator" 
      calculatorPath="/calculator/work"
      category="physics"
      subcategory="Energy"
    >
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: W = F × d</CardTitle>
            <CardDescription>
              Where W is work (J), F is force (N), and d is distance (m)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="force">Force (N)</Label>
                <Input
                  id="force"
                  type="number"
                  step="0.01"
                  placeholder="Enter force"
                  value={force}
                  onChange={(e) => setForce(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="distance">Distance (m)</Label>
                <Input
                  id="distance"
                  type="number"
                  step="0.01"
                  placeholder="Enter distance"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateWork} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-3xl font-bold text-primary flex-1">
                        {result.toFixed(2)} J
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Work (Joules)
                      </p>
                    </div>
                    <CopyResultButton value={`${result.toFixed(2)} J`} />
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["work"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the force applied in Newtons</p>
                <p>2. Enter the distance moved in meters</p>
                <p>3. Click Calculate to get the work done in Joules</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 50 N force over 10 m distance = 500 J work
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
